import qs from 'qs';
import API_URL from '../apiconfig';
import {
  AdditionalServiceRow,
  AppointmentRow,
  OrderRow,
  PaginatedResponse,
  ReportAppointmentsPagination,
  ReportDataParams,
  ReportExportsListResponse,
  ReportFiltersResponse,
  ReportPagination,
  ReportTotals,
} from '../types/reports';
import axios from './api';

export type ReportAppointmentsResponse = {
  result: {
    appointments: AppointmentRow[];
    unlinkedDeposits: AppointmentRow[];
  };
  meta: {
    cursor: {
      appointments: string | null;
      unlinkedDeposits: string | null;
    };
    hasMore: {
      appointments: boolean;
      unlinkedDeposits: boolean;
    };
  };
};

const commonConfig = (params: Record<string, any>) => ({
  params,
  paramsSerializer: (p: Record<string, any>) =>
    qs.stringify(p, { arrayFormat: 'repeat' }),
});

export async function fetchReportTotals(query: ReportDataParams) {
  const response = await axios.get(
    `${API_URL}/v1/reports/totals`,
    commonConfig(query),
  );
  return response.data.result as ReportTotals;
}

export async function fetchReportFilters(query: ReportDataParams) {
  // Strip selectedProducts so the dropdown isn't filtered by its own value.
  const rest = { ...query };
  delete rest.selectedProducts;
  const response = await axios.get(
    `${API_URL}/v1/reports/filters`,
    commonConfig(rest),
  );
  return response.data.result as ReportFiltersResponse;
}

export async function fetchReportAppointmentsPage(
  query: ReportDataParams,
  pagination: ReportAppointmentsPagination,
) {
  const response = await axios.get(
    `${API_URL}/v1/reports/appointments`,
    commonConfig({
      ...query,
      appointmentsCursor: pagination.appointmentsCursor ?? undefined,
      unlinkedDepositsCursor: pagination.unlinkedDepositsCursor ?? undefined,
      skipAppointments: pagination.skipAppointments ?? undefined,
      skipUnlinkedDeposits: pagination.skipUnlinkedDeposits ?? undefined,
      limit: pagination.limit ?? 25,
    }),
  );
  return response.data as ReportAppointmentsResponse;
}

export async function fetchReportAdditionalServicesPage(
  query: ReportDataParams,
  pagination: ReportPagination,
) {
  const response = await axios.get(
    `${API_URL}/v1/reports/additional-services`,
    commonConfig({
      ...query,
      cursor: pagination.cursor ?? undefined,
      limit: pagination.limit ?? 25,
    }),
  );
  return response.data as PaginatedResponse<AdditionalServiceRow>;
}

export async function fetchReportOrdersPage(
  query: ReportDataParams,
  pagination: ReportPagination,
) {
  const response = await axios.get(
    `${API_URL}/v1/reports/orders`,
    commonConfig({
      ...query,
      cursor: pagination.cursor ?? undefined,
      limit: pagination.limit ?? 25,
    }),
  );
  return response.data as PaginatedResponse<OrderRow>;
}

export type ReportExportType =
  | 'APPOINTMENTS'
  | 'ORDERS'
  | 'ADDITIONAL_SERVICES';

export async function createReportExport(
  type: ReportExportType,
  filters: Record<string, unknown>,
) {
  const response = await axios.post(`${API_URL}/v1/reports/exports`, {
    type,
    filters,
  });
  return response.data.result as { id: string };
}

export async function fetchReportExportsPage(
  businessId: string,
  pagination: ReportPagination,
) {
  const response = await axios.get(`${API_URL}/v1/reports/exports`, {
    params: {
      businessId,
      cursor: pagination.cursor ?? undefined,
      limit: pagination.limit ?? 25,
    },
  });
  return response.data as ReportExportsListResponse;
}

export async function fetchReportExportDownloadUrl(id: string) {
  const response = await axios.get(
    `${API_URL}/v1/reports/exports/${id}/download`,
  );
  return response.data.result as { url: string };
}

export async function deleteReportExport(id: string) {
  const response = await axios.delete(`${API_URL}/v1/reports/exports/${id}`);
  return response.data.result as { id: string };
}
